import { useNavigate } from 'react-router-dom';
import { useToastStore, ToastAction, ToastBadges } from '../stores/toast';
import { AlertTriangleIcon, CheckIcon, InfoIcon, XIcon } from './icons';

/** 배지 컴포넌트 */
function ToastBadge({ badges }: { badges: ToastBadges }) {
  if (!badges.orgName && !badges.siteName) return null;

  return (
    <div className="flex items-center gap-1 mr-2">
      {badges.orgName && (
        <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-500/30 text-blue-200 rounded">
          {badges.orgName}
        </span>
      )}
      {badges.siteName && (
        <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-500/30 text-emerald-200 rounded">
          {badges.siteName}
        </span>
      )}
    </div>
  );
}

export function ToastContainer() {
  const navigate = useNavigate();
  const { toasts, hideToast } = useToastStore();

  const handleToastClick = (toastId: string, action?: ToastAction) => {
    if (!action) return;

    // 액션 실행
    if (action.navigateTo) {
      navigate(action.navigateTo);
    }
    if (action.onClick) {
      action.onClick();
    }

    // 토스트 닫기
    hideToast(toastId);
  };

  if (toasts.length === 0) return null;

  // 최대 5개까지만 표시 (최신 순)
  const MAX_VISIBLE_TOASTS = 5;
  const visibleToasts = [...toasts].reverse().slice(0, MAX_VISIBLE_TOASTS);

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[200] pointer-events-none w-auto max-w-lg">
      {/* 최신 토스트가 위에 표시됨 - 각 토스트는 absolute positioning 대신 flow로 배치 */}
      <div className="flex flex-col items-center space-y-3">
        {visibleToasts.map((toast) => {
          const hasAction = toast.action?.navigateTo || toast.action?.onClick;

          return (
            <div
              key={toast.id}
              onClick={hasAction ? () => handleToastClick(toast.id, toast.action) : undefined}
              className={`flex items-center gap-2 px-4 py-3 bg-ink-800 text-white rounded-card shadow-lg border border-ink-700 pointer-events-auto ${
                hasAction ? 'cursor-pointer hover:bg-ink-700' : ''
              }`}
            >
            {toast.type === 'error' && (
              <AlertTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            )}
            {toast.type === 'success' && (
              <CheckIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
            )}
            {toast.type === 'info' && (
              <InfoIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
            )}
            {toast.type === 'warning' && (
              <AlertTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0 flex items-center flex-wrap">
              {toast.badges && <ToastBadge badges={toast.badges} />}
              <span className="text-sm">{toast.message}</span>
              {hasAction && (
                <span className="text-xs text-ink-400 ml-2">(클릭하여 이동)</span>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                hideToast(toast.id);
              }}
              className="ml-2 text-ink-400 hover:text-white flex-shrink-0"
            >
              <XIcon className="h-4 w-4" />
            </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
